import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DEMO_DAY0_KEY, isDay0Forced, isFirstAccess } from "../utils/day0";
import { SSO_AUTH_METHOD_KEY, type AuthMethod } from "../constants/auth";
import { hasValidSession } from "../utils/auth";
import {
  authApi,
  decodeJwtPayload,
  isTokenExpired,
  type LoginResponse,
  type ResetPasswordResponse,
  type SetupMfaResponse,
  type ConfirmMfaResponse,
  type ForgotPasswordResponse,
  type ConfirmForgotPasswordResponse,
} from "@repo/api";
import { Logger } from "@saviynt/common";

interface AuthContextType {
  isAuthenticated: boolean;
  isAuthChecked: boolean;
  isLoading: boolean;
  userEmail: string | null;
  /** Friendly first name: JWT `given_name` claim, or first token of the username (capitalized). */
  userDisplayName: string | null;
  /** Login email: JWT `email` claim, or the username if it contains `@`. */
  userLoginEmail: string | null;
  completeAuth: () => void;
  login: (username: string, password: string) => Promise<LoginResponse>;
  verifyMfa: (
    username: string,
    mfaCode: string,
    session: string,
    challengeName: string,
  ) => Promise<LoginResponse>;
  resetPassword: (
    username: string,
    newPassword: string,
    session: string,
  ) => Promise<ResetPasswordResponse>;
  setupMfa: (username: string, session: string) => Promise<SetupMfaResponse>;
  confirmMfa: (
    username: string,
    mfaCode: string,
    session: string,
  ) => Promise<ConfirmMfaResponse>;
  forgotPassword: (username: string) => Promise<ForgotPasswordResponse>;
  confirmForgotPassword: (
    username: string,
    confirmationCode: string,
    newPassword: string,
  ) => Promise<ConfirmForgotPasswordResponse>;
  loginWithSSO: (code: string, state: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  /** Post-auth destination: `/` (Day-0) on first-ever login, else `/dashboard`. */
  getHomeRoute: () => string;
  /**
   * Re-read tokens from localStorage into React state. Used when tokens exist
   * but `isAuthenticated` was cleared (e.g. a spurious auth reset) so `/` and
   * `/login` do not redirect-loop with a blank page.
   */
  syncFromStorage: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function capitalizeFirst(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function deriveUserInfo(
  username: string | null,
  accessTokenKey: string,
): { displayName: string | null; loginEmail: string | null } {
  if (!username) return { displayName: null, loginEmail: null };
  const token = localStorage.getItem(accessTokenKey);
  const payload = token ? decodeJwtPayload(token) : null;
  const givenName = payload?.given_name ? String(payload.given_name) : "";
  const jwtEmail = payload?.email ? String(payload.email) : "";
  const displayName =
    givenName || capitalizeFirst(username.split("@")[0].split(/[._-]/)[0]);
  const loginEmail = jwtEmail || (username.includes("@") ? username : null);
  return { displayName, loginEmail };
}

const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const USERNAME_KEY = "username";
const SESSION_KEY = "session";
const SSO_TEST_PENDING_KEY = "sso-test-pending";
const TENANT_ID_KEY = "tenantId";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [username, setUsername] = useState<string | null>(() =>
    localStorage.getItem(USERNAME_KEY),
  );

  // Whether the most recent auth response indicated a first-ever access (empty
  // lastAccessedAt). Defaults to false: without a fresh login saying otherwise
  // (e.g. a refresh or a direct `/` visit), the user is treated as returning
  // and never sees Day-0. Set from the auth response; a ref so it's read
  // synchronously in onSuccess. Not persisted — the server owns lastAccessedAt.
  const firstAccessRef = useRef(false);

  // Gate the Day-0 first-access flag on the correct auth path. Each login
  // method should only set firstAccessRef if its own response is authoritative
  // (i.e. the stored method is NOT the other path's method).
  function maybeSetFirstAccess(
    lastAccessedAt: number | null | undefined,
    blockedMethod: AuthMethod,
  ) {
    const ssoMethod = sessionStorage.getItem(SSO_AUTH_METHOD_KEY);
    if (ssoMethod !== blockedMethod) {
      firstAccessRef.current = isFirstAccess(lastAccessedAt);
    }
    sessionStorage.removeItem(SSO_AUTH_METHOD_KEY);
  }

  /**
   * Where to send the user (and what `/` renders): the Day-0 landing (`/`) only
   * on a genuine first-ever access, or when the DEMO_DAY0 QA override is set;
   * otherwise the dashboard. A non-empty lastAccessedAt ⇒ never Day-0.
   */
  const getHomeRoute = useCallback(
    () => (isDay0Forced() || firstAccessRef.current ? "/" : "/dashboard"),
    [],
  );

  useEffect(() => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      if (isTokenExpired(token)) {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (refreshToken) {
          // Access token expired but refresh token is available.
          // Stay authenticated — fetchClient's proactive refresh interceptor
          // will obtain a fresh access token on the next API call.
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem(ACCESS_TOKEN_KEY);
          localStorage.removeItem(USERNAME_KEY);
          localStorage.removeItem(SESSION_KEY);
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(true);
      }
    }
    setIsAuthChecked(true);
  }, []);

  // Cross-tab sync + forced logout from fetchClient on token refresh failure.
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ACCESS_TOKEN_KEY) {
        setIsAuthenticated(!!e.newValue);
        // Username is written in the same login turn; read current value so
        // the other tab's shell/header reflects the signed-in user.
        setUsername(e.newValue ? localStorage.getItem(USERNAME_KEY) : null);
      }
    };
    const handleUnauthorized = () => {
      setIsAuthenticated(false);
      setUsername(null);
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
    };
  }, []);

  const login = useCallback(
    async (username: string, password: string): Promise<LoginResponse> => {
      setIsLoading(true);
      try {
        const response = await authApi.login({ username, password });
        if (response.session) {
          localStorage.setItem(SESSION_KEY, response.session);
        }
        if (response.success && response.accessToken && response.refreshToken) {
          localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
          localStorage.setItem(USERNAME_KEY, username);
          localStorage.removeItem(SESSION_KEY);
          // Day-0: only applies to the Password path from sso/initiate.
          // SSO users get firstAccessRef set in loginWithSSO (sso/callback).
          maybeSetFirstAccess(response.lastAccessedAt, "SSO");
          if (response.selectedTenantId != null) {
            localStorage.setItem(
              TENANT_ID_KEY,
              String(response.selectedTenantId),
            );
          }
          setIsAuthenticated(true);
          setUsername(username);
        }
        return response;
      } catch (error) {
        Logger.error("Login failed:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const verifyMfa = useCallback(
    async (
      username: string,
      mfaCode: string,
      session: string,
      challengeName: string,
    ): Promise<LoginResponse> => {
      setIsLoading(true);
      try {
        const response = await authApi.verifyMfa({
          username,
          mfaCode,
          session,
          challengeName: challengeName as "SMS_MFA" | "SOFTWARE_TOKEN_MFA",
        });
        if (response.success && response.accessToken && response.refreshToken) {
          localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
          localStorage.setItem(USERNAME_KEY, username);
          localStorage.removeItem(SESSION_KEY);
          maybeSetFirstAccess(response.lastAccessedAt, "SSO");
          if (response.selectedTenantId != null) {
            localStorage.setItem(
              TENANT_ID_KEY,
              String(response.selectedTenantId),
            );
          }
          setIsAuthenticated(true);
          setUsername(username);
        }
        return response;
      } catch (error) {
        Logger.error("MFA verification failed:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const resetPassword = useCallback(
    async (
      username: string,
      newPassword: string,
      session: string,
    ): Promise<ResetPasswordResponse> => {
      setIsLoading(true);
      try {
        const response = await authApi.resetPassword({
          username,
          newPassword,
          session,
        });
        if (response.session) {
          localStorage.setItem(SESSION_KEY, response.session);
        }
        return response;
      } catch (error) {
        Logger.error("Password reset failed:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const setupMfa = useCallback(
    async (username: string, session: string): Promise<SetupMfaResponse> => {
      setIsLoading(true);
      try {
        const response = await authApi.setupMfa({
          username,
          session,
          mfaType: "TOTP",
        });
        if (response.session) {
          localStorage.setItem(SESSION_KEY, response.session);
        }
        return response;
      } catch (error) {
        Logger.error("MFA setup failed:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const confirmMfa = useCallback(
    async (
      username: string,
      mfaCode: string,
      session: string,
    ): Promise<ConfirmMfaResponse> => {
      setIsLoading(true);
      try {
        const response = await authApi.confirmMfa({
          username,
          mfaCode,
          session,
          mfaType: "TOTP",
        });
        if (response.success && response.accessToken && response.refreshToken) {
          localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
          localStorage.setItem(USERNAME_KEY, username);
          localStorage.removeItem(SESSION_KEY);
          maybeSetFirstAccess(response.lastAccessedAt, "SSO");
          // Do NOT set isAuthenticated here — caller shows success step first,
          // then calls completeAuth() before navigating.
          setUsername(username);
        }
        return response;
      } catch (error) {
        Logger.error("MFA confirmation failed:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const completeAuth = useCallback(() => {
    setIsAuthenticated(true);
  }, []);

  const syncFromStorage = useCallback(() => {
    if (hasValidSession()) {
      setIsAuthenticated(true);
      setUsername(localStorage.getItem(USERNAME_KEY));
    } else {
      setIsAuthenticated(false);
      setUsername(null);
    }
  }, []);

  const forgotPassword = useCallback(
    async (username: string): Promise<ForgotPasswordResponse> => {
      return authApi.forgotPassword({ username });
    },
    [],
  );

  const confirmForgotPassword = useCallback(
    async (
      username: string,
      confirmationCode: string,
      newPassword: string,
    ): Promise<ConfirmForgotPasswordResponse> => {
      return authApi.confirmForgotPassword({
        username,
        confirmationCode,
        newPassword,
      });
    },
    [],
  );

  const loginWithSSO = useCallback(
    async (code: string, state: string): Promise<LoginResponse> => {
      setIsLoading(true);
      try {
        const response = await authApi.ssoCallback({ code, state });
        if (response.accessToken && response.refreshToken) {
          localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
          // Day-0: only applies to the SSO path from sso/initiate.
          // Default to SSO when method is absent — loginWithSSO is only ever
          // called from SSOCallbackPage, which is only reachable via IdP redirect.
          maybeSetFirstAccess(response.lastAccessedAt, "PASSWORD");
          const pendingEmail = sessionStorage.getItem("sso_pending_email");
          sessionStorage.removeItem("sso_pending_email");
          const payload = decodeJwtPayload(response.accessToken);
          const resolvedUsername =
            pendingEmail ||
            (payload?.email as string) ||
            (payload?.username as string) ||
            "";
          if (resolvedUsername) {
            localStorage.setItem(USERNAME_KEY, resolvedUsername);
            setUsername(resolvedUsername);
          }
          if (response.selectedTenantId != null) {
            localStorage.setItem(
              TENANT_ID_KEY,
              String(response.selectedTenantId),
            );
          }
          setIsAuthenticated(true);
        }
        return response;
      } catch (error) {
        Logger.error("SSO callback failed:", error);
        sessionStorage.removeItem("sso_pending_email");
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    let redirectHref = "/login";
    try {
      const response = await authApi.logout();
      if (response.logoutUrl) {
        try {
          const parsed = new URL(response.logoutUrl);
          if (parsed.protocol === "https:") {
            redirectHref = encodeURI(parsed.toString());
          }
        } catch {
          // malformed URL — fall back to /login
        }
      }
    } catch (error) {
      Logger.error("Logout failed:", error);
    } finally {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USERNAME_KEY);
      localStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(SSO_TEST_PENDING_KEY);
      localStorage.removeItem(TENANT_ID_KEY);
      // Clear the QA Day-0 override so it can't bleed into the next real session.
      localStorage.removeItem(DEMO_DAY0_KEY);
      sessionStorage.removeItem(SSO_AUTH_METHOD_KEY);
      window.location.href = redirectHref;
    }
  }, []);

  const { displayName: userDisplayName, loginEmail: userLoginEmail } = useMemo(
    () => deriveUserInfo(username, ACCESS_TOKEN_KEY),
    [username],
  );

  const value = useMemo(
    () => ({
      isAuthenticated,
      isAuthChecked,
      isLoading,
      userEmail: username,
      userDisplayName,
      userLoginEmail,
      completeAuth,
      login,
      verifyMfa,
      resetPassword,
      setupMfa,
      confirmMfa,
      forgotPassword,
      confirmForgotPassword,
      loginWithSSO,
      logout,
      getHomeRoute,
      syncFromStorage,
    }),
    [
      isAuthenticated,
      isAuthChecked,
      isLoading,
      username,
      userDisplayName,
      userLoginEmail,
      completeAuth,
      login,
      verifyMfa,
      resetPassword,
      setupMfa,
      confirmMfa,
      forgotPassword,
      confirmForgotPassword,
      loginWithSSO,
      logout,
      getHomeRoute,
      syncFromStorage,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
